import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { PageInfoButton } from "@/components/common/page-info-button";
import { ElapsedTimeNote } from "@/components/common/elapsed-time-note";
import { ThreeClocksPanel } from "@/components/common/three-clocks-panel";
import { DailyActivityGrid } from "@/components/admin/DailyActivityGrid";
import { KpiSummaryCards } from "@/components/admin/KpiSummaryCards";
import { UserFilterCombobox } from "@/components/admin/UserFilterCombobox";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { ExportBar } from "@/components/admin/ExportBar";
import {
  PeriodSelector,
  defaultPeriodState,
  type PeriodState,
} from "@/components/admin/PeriodSelector";
import { useAdminPeriodActivity } from "@/hooks/queries/use-admin-activity";
import { generateCSV, triggerPrint } from "@/lib/export-utils";
import { formatDuration } from "@/lib/format-utils";
import { type SortKey, type SortDir, sortActivityUsers, getActivityTotal } from "@/lib/activity-sort-utils";

export function ActivityDashboardPage() {
  usePageTitle("Activity Dashboard");
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodState>(defaultPeriodState);
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const activeUserIds = selectedUserIds.length > 0 ? selectedUserIds : undefined;

  const { data, isLoading, isError } = useAdminPeriodActivity(
    period.startDate,
    period.endDate,
    activeUserIds
  );

  /**
   * Aggregate ELAPSED minutes across the visible rows.
   *
   * A sum of ONE clock only. Null when no row in the period carries an elapsed
   * measurement, so the panel renders an explicit not-measured state instead of
   * a zero that would read as "no time was spent".
   */
  const aggregateElapsedMinutes =
    data && data.length > 0
      ? data.reduce((sum, row) => sum + row.totalElapsedMinutes, 0)
      : null;

  const handleUserClick = useCallback(
    (userId: string) => {
      navigate(`/admin/activity/user/${userId}`, {
        state: {
          periodType: period.periodType,
          startDate: period.startDate,
          endDate: period.endDate,
        },
      });
    },
    [navigate, period.periodType, period.startDate, period.endDate]
  );

  const handleSortChange = useCallback((key: SortKey, dir: SortDir) => {
    setSortKey(key);
    setSortDir(dir);
  }, []);

  const handleDownloadCSV = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = [
      "User",
      "Email",
      "Epic Requests",
      "Epics Created",
      "Features Done",
      "Tasks Done",
      "Epics Done",
      "Total",
      "Elapsed",
      "Avg Elapsed/Item",
      "AI Session Time",
    ];

    const sortedData = sortActivityUsers(data, sortKey, sortDir);

    const rows = sortedData.map((row) => [
      row.userName,
      row.userEmail,
      String(row.epicRequestsCreated),
      String(row.epicsCreated),
      String(row.featuresCompleted),
      String(row.tasksCompleted),
      String(row.epicsCompleted),
      String(getActivityTotal(row)),
      formatDuration(row.totalElapsedMinutes),
      formatDuration(row.avgElapsedPerItem),
      formatDuration(row.aiSessionMinutes),
    ]);

    const ts = new Date().toISOString().slice(0, 10);
    generateCSV(headers, rows, `activity-dashboard-${ts}.csv`);
  }, [data, sortKey, sortDir]);

  return (
    <div className="pt-4 px-4 md:px-0">
      {/* Print-only header */}
      <div className="print-header hidden">
        <h1>Activity Dashboard</h1>
        <p>
          {period.startDate} — {period.endDate} · Exported{" "}
          {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className="glass-card rounded-2xl p-4 md:p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            <h1 className="text-lg font-medium">Activity</h1>
            <PageInfoButton title="Admin" />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {data && data.length > 0 && (
              <ExportBar
                onDownloadCSV={handleDownloadCSV}
                onPrint={triggerPrint}
              />
            )}
            <UserFilterCombobox value={selectedUserIds} onChange={setSelectedUserIds} />
            {/* Period selector */}
            <PeriodSelector value={period} onChange={setPeriod} />
          </div>
        </div>

        <AdminTabs />

        {/* KPI Summary */}
        <div className="mb-3">
          <KpiSummaryCards startDate={period.startDate} endDate={period.endDate} {...(activeUserIds ? { userIds: activeUserIds } : {})} />
        </div>

        {/* What "Elapsed" means — always visible, never hover-only */}
        <ElapsedTimeNote className="mb-3" />

        {/* Elapsed, active and compute as three separate values in the same
            view, each labelled with the question it answers. They are never
            summed, and active time is not a corrected elapsed time. */}
        <ThreeClocksPanel className="mb-6" elapsedMinutes={aggregateElapsedMinutes} />

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading activity...
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-12 text-destructive">
            Failed to load activity data.
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            No activity in this period.
          </div>
        ) : (
          <DailyActivityGrid data={data} sortKey={sortKey} sortDir={sortDir} onSortChange={handleSortChange} onUserClick={handleUserClick} />
        )}
      </div>
    </div>
  );
}
